/**
 * OrderCard — Plenty-Stil-Karte fuer eine GroupedOrder (1 Karte pro Zeile).
 *
 * Phase 5:
 *   - 1 Karte pro Zeile mit allen Card-Feldern (Stammdaten + Adressen + Tags + Items inline)
 *   - Tab-spezifisches Layout: Spedition zeigt Alarm-Farbe (5W gelb, 6W rot)
 *   - Type-4-Kinder eingeklappt unter main
 */

import { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, Truck, MapPin, CreditCard, Tag } from 'lucide-react';
import { GroupedOrder, OrderListItem } from '../lib/groupOrders';

interface OrderCardProps {
  group: GroupedOrder;
  kategorie: 'paket' | 'spedition';
  onSelect: (id: number) => void;
}

const statusBadge: Record<string, { label: string; cls: string }> = {
  open: { label: 'Offen', cls: 'bg-gray-100 text-gray-700' },
  awaiting_package: { label: 'Wartet auf Paket', cls: 'bg-blue-50 text-blue-700' },
  carrier: { label: 'Bei Carrier', cls: 'bg-yellow-50 text-yellow-800' },
  manual_only: { label: 'Nur manuell', cls: 'bg-purple-50 text-purple-700' },
  done: { label: 'Abgeschlossen', cls: 'bg-green-50 text-green-700' },
  closed_manually: { label: 'Manuell geschlossen', cls: 'bg-gray-200 text-gray-500' },
};

const internalStatusLabel: Record<string, string> = {
  pre_transit: 'Label gedruckt',
  in_transit: 'Unterwegs',
  out_for_delivery: 'In Zustellung',
  delivered: 'Zugestellt',
  exception: 'Problem',
  returned: 'Retoure',
  unknown: 'Unbekannt',
};

const SPEDITION_STATUS_IDS = new Set<number>([5.65, 5.66]);
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Spedition-Alarm-Berechnung: nur fuer Status 5.65 / 5.66.
 * < 35 Tage → null (kein Alarm)
 * 35-41 Tage → 'yellow' (5+ Wochen Warten)
 * >= 42 Tage → 'red' (6+ Wochen Warten)
 */
function speditionAlarm(o: OrderListItem): 'yellow' | 'red' | null {
  if (o.orderStatusId == null || !SPEDITION_STATUS_IDS.has(Number(o.orderStatusId))) return null;
  const enteredAt = o.statusEnteredAt || o.orderReceivedAt;
  if (!enteredAt) return null;
  const daysSince = Math.floor((Date.now() - new Date(enteredAt).getTime()) / MS_PER_DAY);
  if (daysSince >= 42) return 'red';
  if (daysSince >= 35) return 'yellow';
  return null;
}

function formatAddress(addr: Record<string, any> | null | undefined): string {
  if (!addr) return '–';
  // Adresse ist nur ein Stub (nur addressId, noch nicht hydriert)
  if (typeof addr === 'object' && addr.addressId && !addr.name1 && !addr.postCode) {
    return `ID ${addr.addressId} (wird beim Oeffnen geladen…)`;
  }
  const parts: string[] = [];
  const name = [addr.name1, addr.name2].filter(Boolean).join(' ').trim();
  if (name) parts.push(name);
  const place = [addr.postCode, addr.town].filter(Boolean).join(' ').trim();
  if (addr.street) parts.push(`${addr.street}${addr.houseNumber ? ' ' + addr.houseNumber : ''}`);
  if (place) parts.push(place);
  return parts.length > 0 ? parts.join(', ') : '–';
}

function renderItems(items: OrderListItem['items'], maxShown: number): React.JSX.Element | null {
  if (!items || items.length === 0) return null;
  const shown = items.slice(0, maxShown);
  const remaining = items.length - shown.length;
  return (
    <div className="mt-2 text-xs">
      <table className="w-full">
        <tbody className="divide-y divide-[#DAD9D9]">
          {shown.map((item) => (
            <tr key={item.id} className="text-gray-700">
              <td className="py-1 pr-2 font-mono text-gray-600 w-12">{item.quantity ?? '?'}x</td>
              <td className="py-1 pr-2 truncate" title={item.itemName ?? ''}>{item.itemName ?? 'unbenannt'}</td>
              <td className="py-1 font-mono text-gray-500 text-[10px] whitespace-nowrap">
                {item.variationNumber ?? `var ${item.variationId}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {remaining > 0 && (
        <div className="text-[11px] text-gray-500 italic mt-1">+{remaining} weitere Position{remaining === 1 ? '' : 'en'}</div>
      )}
    </div>
  );
}

function renderOrderRow(o: OrderListItem, isChild: boolean, showItems: boolean): React.JSX.Element {
  const badge = statusBadge[o.trackingStatus] ?? { label: o.trackingStatus, cls: 'bg-gray-100 text-gray-700' };
  return (
    <div className={`${isChild ? 'pl-4 border-l-2 border-[#DAD9D9]' : ''}`}>
      {/* Zeile 1: Auftragsnr + Status-Pills */}
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <span className="font-mono text-base font-semibold text-[#1A1A1A]">#{o.plentyOrderId}</span>
        {o.orderType === 4 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-semibold">
            LIEFERAUFTRAG
          </span>
        )}
        {o.orderStatusName && (
          <span className="text-[11px] px-2 py-0.5 rounded bg-[#E9FF57] text-[#1A1A1A] font-medium">
            {o.orderStatusName}
          </span>
        )}
        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${badge.cls}`}>
          {badge.label}
        </span>
      </div>

      {/* Zeile 2: Stammdaten kompakt */}
      <div className="text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-0.5 mb-1.5">
        <span>Eingang: <span className="text-[#1A1A1A] font-medium">{formatDate(o.orderReceivedAt)}</span></span>
        {o.contactId && <span>Kunde: <span className="text-[#1A1A1A] font-medium">{o.contactId}</span></span>}
        {o.paymentMethodName && (
          <span className="flex items-center gap-1">
            <CreditCard className="w-3 h-3" />
            <span className="text-[#1A1A1A]">{o.paymentMethodName}</span>
          </span>
        )}
        {o.orderSource && <span>Quelle: <span className="text-[#1A1A1A]">{o.orderSource}</span></span>}
      </div>

      {/* Zeile 3: Adressen */}
      <div className="text-xs text-gray-600 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 mb-1.5">
        <span className="flex items-start gap-1">
          <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span><span className="text-gray-400">Rechnung:</span> <span className="text-[#1A1A1A]">{formatAddress(o.billingAddress)}</span></span>
        </span>
        <span className="flex items-start gap-1">
          <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span><span className="text-gray-400">Lieferung:</span> <span className="text-[#1A1A1A]">{formatAddress(o.shippingAddress)}</span></span>
        </span>
      </div>

      {/* Zeile 4: Versand + Tags */}
      <div className="text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1 mb-1.5">
        <span className="flex items-center gap-1">
          <Truck className="w-3 h-3" />
          <span className="text-[#1A1A1A]">{o.shippingProfileName ?? (o.shippingProfileId ? `Profil ${o.shippingProfileId}` : '–')}</span>
        </span>
        {o.packageNumber && (
          <span className="font-mono text-[11px] text-[#1A1A1A]">{o.packageNumber}</span>
        )}
        {o.tags && o.tags.length > 0 && (
          <span className="flex items-center gap-1 flex-wrap">
            <Tag className="w-3 h-3" />
            {o.tags.map((t) => (
              <span key={t.id} className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 text-[10px]">
                {t.name}
              </span>
            ))}
          </span>
        )}
        {o.warenausgangBookedAt && (
          <span className="text-[11px] text-gray-500">Warenausgang: {formatDateTime(o.warenausgangBookedAt)}</span>
        )}
      </div>

      {/* Zeile 5: Carrier-Event */}
      {o.lastInternalStatus && (
        <div className="text-xs text-gray-700 mb-1">
          Letzter Event: <span className="font-medium">{internalStatusLabel[o.lastInternalStatus] ?? o.lastInternalStatus}</span>
          {o.lastEventAt && <span className="text-gray-500"> · {formatDateTime(o.lastEventAt)}</span>}
        </div>
      )}

      {/* Artikel-Tabelle inline */}
      {showItems && renderItems(o.items, 3)}
    </div>
  );
}

export default function OrderCard({ group, kategorie, onSelect }: OrderCardProps) {
  const [showChildren, setShowChildren] = useState(false);
  const stuck = group.main.hasOpenNotification;
  const isOrphanType4 =
    group.main.orderType === 4 &&
    !group.main.parentInScope &&
    group.main.parentPlentyOrderId != null;

  // Phase 5: Spedition-Alarm-Farbe (gelb 5W, rot 6W)
  const alarm = kategorie === 'spedition' ? speditionAlarm(group.main) : null;

  // Border-Styles je nach Alarm
  let borderCls = 'border border-[#DAD9D9]';
  if (alarm === 'red') borderCls = 'border-l-4 border-l-red-500 border-t border-r border-b border-t-[#DAD9D9] border-r-[#DAD9D9] border-b-[#DAD9D9] bg-red-50/30';
  else if (alarm === 'yellow') borderCls = 'border-l-4 border-l-yellow-500 border-t border-r border-b border-t-[#DAD9D9] border-r-[#DAD9D9] border-b-[#DAD9D9] bg-yellow-50/30';
  else if (stuck) borderCls = 'border-l-4 border-l-orange-500 border-t border-r border-b border-t-[#DAD9D9] border-r-[#DAD9D9] border-b-[#DAD9D9]';

  return (
    <div
      className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden ${borderCls}`}
      onClick={() => onSelect(group.main.id)}
    >
      <div className="p-4">
        {/* Alarm-Banner Spedition */}
        {alarm === 'red' && (
          <div className="flex items-center gap-1.5 text-xs text-red-700 mb-2 font-semibold">
            <AlertCircle className="w-3.5 h-3.5" />
            6+ Wochen im Spedition-Status — Hersteller anrufen!
          </div>
        )}
        {alarm === 'yellow' && (
          <div className="flex items-center gap-1.5 text-xs text-yellow-800 mb-2 font-semibold">
            <AlertCircle className="w-3.5 h-3.5" />
            5+ Wochen im Spedition-Status
          </div>
        )}

        {/* Stuck-Banner */}
        {stuck && !alarm && (
          <div className="flex items-center gap-1.5 text-xs text-orange-700 mb-2 font-semibold">
            <AlertCircle className="w-3.5 h-3.5" />
            Stuck — offene Notification
          </div>
        )}

        {/* Verwaister Type-4 Cross-Link */}
        {isOrphanType4 && (
          <div className="text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded mb-2">
            ↗ Hauptauftrag #{group.main.parentPlentyOrderId} liegt ausserhalb Tracking-Zeitraum
          </div>
        )}

        {/* Haupt-Auftrag */}
        {renderOrderRow(group.main, false, true)}

        {/* Eingeklappte Type-4-Kinder */}
        {group.children.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#DAD9D9]">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowChildren((v) => !v);
              }}
              className="flex items-center gap-1 text-xs text-[#1A1A1A] hover:underline"
            >
              {showChildren ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {group.children.length}{' '}
              {group.children.length === 1 ? 'Lieferauftrag' : 'Lieferauftraege'}{' '}
              {showChildren ? 'ausblenden' : 'anzeigen'}
            </button>
            {showChildren && (
              <div className="mt-2 space-y-3">
                {group.children.map((child) => (
                  <div
                    key={child.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(child.id);
                    }}
                  >
                    {renderOrderRow(child, true, false)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
